'use client'
import { cn } from '../helpers/index.js'
import { Slot } from '@radix-ui/react-slot'
import { PlusIcon, MinusIcon } from '@radix-ui/react-icons'
import React, { useState, useEffect, forwardRef } from 'react'
import type { ChangeEvent, KeyboardEvent, FocusEvent } from 'react'
import { type VariantProps, cva } from 'class-variance-authority'
import type { ComponentPropsWithout, RemovedProps } from '../helpers/component-props.js'

// 定义 InputNumber 的样式变体
const InputNumberVariants = cva('size-full border bg-transparent text-center text-base outline-none', {
  variants: {
    status: {
      default: 'border-lines-1 group-hover:border-lines-2',
      success: 'border-lines-3 group-hover:border-lines-3',
      warning: 'border-lines-4 group-hover:border-lines-4',
      error: 'border-lines-5 group-hover:border-lines-5',
    },
    disabled: {
      true: 'border-lines-1 group-hover:border-lines-1 text-info-quaternary cursor-not-allowed',
    },
  },
})

type ColorType = 'default' | 'success' | 'warning' | 'error' | null

// 定义组件类型，继承 div 标签标准属性
type InputNumberElement = React.ElementRef<'div'>

type InputNumberProps = {
  /** 定义子组件作为父组件的类型,详细使用方式请参考radix-ui */
  asChild?: boolean
  /** 类名*/
  className?: string
  /** 输入框下方提示文本，会根据不同的 status 呈现不同的样式*/
  tips?: string
  /** 文本框状态*/
  status?: ColorType
  /** 数字输入框的值 */
  value?: number
  /** 数字输入框的初始值 */
  defaultValue?: number
  /** 数值改变步数 */
  step?: number
  /** 最小值 */
  min?: number
  /** 最大值 */
  max?: number
  /** 是否禁用*/
  disabled?: boolean
  /** 是否只读*/
  readonly?: boolean
  /** 样式*/
  style?: React.CSSProperties
  /** 值变化时触发*/
  onChange?: (value: number) => void
  /** 失去焦点时触发*/
  onBlur?: (value: number, e: FocusEvent) => void
  /** 按下回车键时触发*/
  onEnter?: (value: number, e: KeyboardEvent) => void
} & VariantProps<typeof InputNumberVariants> &
  ComponentPropsWithout<'div', RemovedProps>

/**
 * InputNumber - 数字输入框
 *
 * @description 专用于数字输入的组件，支持步进调节、最值限制、状态提示和键盘操作
 */
const InputNumber = forwardRef<InputNumberElement, InputNumberProps>(
  (
    {
      value,
      defaultValue = 0,
      step = 1,
      min = 0,
      max,
      tips,
      style,
      children,
      asChild = false,
      disabled = false,
      readonly = false,
      status = 'default',
      className,
      onChange,
      onBlur,
      onEnter,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<number | string>(defaultValue)

    const Comp = asChild ? Slot : 'div'

    const baseClassName = cn('border-lines-1 flex size-12 items-center justify-center border text-base', {
      'group-hover:border-lines-2': !disabled,
      'text-info-quaternary cursor-not-allowed': disabled,
    })

    const textColor = (type: ColorType, flag: boolean) => {
      if (flag) return 'text-lines-1'
      return {
        default: 'text-lines-1',
        success: 'text-lines-3',
        warning: 'text-lines-4',
        error: 'text-lines-5',
      }[type || 'default']
    }

    const isValue = (params: string) => {
      return params === '' || /^[0-9]+$/.test(params)
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled || readonly) return
      const value = e.target.value
      // 允许空字符串，或仅包含整数的字符串
      if (isValue(value)) {
        setInputValue(value)
        if (value !== '') {
          let newValue = Number(value)
          if (max !== undefined && newValue > max) {
            newValue = max
          }
          if (min !== undefined && newValue < min) {
            newValue = min
          }
          onChange?.(newValue)
        }
      }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (disabled || readonly) return
      if (inputValue === '' || isNaN(Number(inputValue))) {
        const newValue = min !== undefined ? min : 0
        setInputValue(newValue)
        onBlur?.(newValue, e)
      } else {
        const newValue = Number(inputValue)
        setInputValue(newValue)
        onBlur?.(newValue, e)
      }
    }

    const increment = () => {
      if (disabled || readonly) return
      let newValue = Number(inputValue) + (step ? Math.ceil(Number(step)) : 1)
      if (max !== undefined && newValue > max) {
        newValue = max
      }
      setInputValue(newValue)
      onChange?.(newValue)
    }

    const decrement = () => {
      if (disabled || readonly) return
      let newValue = Number(inputValue) - (step ? Math.ceil(Number(step)) : 1)
      if (min !== undefined && newValue < min) {
        newValue = min
      }
      setInputValue(newValue)
      onChange?.(newValue)
    }

    const handleKeyDown = (e: any) => {
      if (disabled || readonly) return
      if (e.key === 'Enter') {
        const initalValue = e.target?.value
        onEnter?.(initalValue, e)
      }
    }

    useEffect(() => {
      setInputValue(value || 0)
    }, [value])

    return (
      <Comp
        ref={ref}
        {...(style ? { style: style } : null)}
        className={cn({ 'group flex items-center': !asChild }, className)}
      >
        {asChild ? (
          children
        ) : (
          <>
            <button onClick={increment} className={baseClassName}>
              <PlusIcon />
            </button>
            <div className="relative mx-1.5 h-[52px] w-[105px]">
              <input
                type="text"
                value={inputValue}
                disabled={disabled}
                readOnly={readonly}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onChange={handleInputChange}
                className={cn(InputNumberVariants({ status, disabled }))}
              />
              {tips ? (
                <span className={cn(textColor(status, disabled), 'absolute -bottom-4 left-0 w-full truncate text-xs')}>
                  {tips}
                </span>
              ) : null}
            </div>
            <button onClick={decrement} className={baseClassName}>
              <MinusIcon />
            </button>
          </>
        )}
      </Comp>
    )
  }
)

InputNumber.displayName = 'InputNumber'

export default InputNumber
export type { InputNumberProps }
